import { toolkitUrls } from '@/links'

export const CUSTOMIZATIONS = {
  GOALS: 'goals',
  AUTO_REVIEW: 'autoReview'
}

const GOAL_PAGES = [
  /^\/goal\//,
  /^\/library\/goal\//,
  /^\/share\/goal\//,
  /^\/anchor-page\//,
  /^\/library\/anchor-page\//,
  /^\/share\/anchor-page\//
]

// Each category is one pre-configured customization a demo can be run as, backed by a real content
// profile that implements that convention — goal categories come from the prompts in the
// goal-prompts Airtable base, auto-review categories from the criteria sets in review-criteria.
// The picker offers these categories rather than the full content profile list, so the profile
// behind a category is an implementation detail of the demo, not a choice the presenter makes.
// Every district profile here is released, so those categories need no beta mode to demo. The
// engineering-testing entry is the exception — see its comment below.
//
// `pagePatterns` names the routes where the customization actually takes effect. The modal lists
// every customization wherever it is opened, and disables the ones this page cannot run, pointing
// at where they can be.
export const CUSTOMIZATION_CATALOG = [
  {
    key: CUSTOMIZATIONS.GOALS,
    title: 'Goal Customizations',
    description: 'Apply a pre-configured customization to goals and anchor pages.',
    demoLinks: [{ label: 'Browse goals to run this demo', url: toolkitUrls.browse }],
    pagePatterns: GOAL_PAGES,
    categories: [
      // Hawaii's ABCD+T skeleton: "In ([X]) weeks, [Condition], (name) will ..."
      { label: 'Week-based goal', profileId: 'hawaii' },
      // Minnesota requires an explicit current-and-target performance statement on every goal.
      { label: 'Baseline reference', profileId: 'minnesota-draft' },
      // Irvine rewrites only the closing measurement criteria, leaving the rest of the goal alone.
      { label: 'Measurement criteria', profileId: 'california-irvine' },
      // Tennessee restructures the whole goal into the state's Measurable Annual Goal format.
      { label: 'State compliance format', profileId: 'tennessee' },
      // Content Production's scratch profile, where a prospective district's format is staged
      // ahead of a partner meeting. Its prompt is beta-only rather than released, so this is the
      // one category that needs the presenter's Beta toggle on: with Beta off the backend reports
      // the goal as not customizable and the page renders the unmodified text with no error.
      { label: 'Separate annual and baseline', profileId: 'engineering-testing' }
    ]
  },
  {
    key: CUSTOMIZATIONS.AUTO_REVIEW,
    title: 'Auto-Review Rubric',
    description:
      "Review goals, present levels and impact statements against a district's rubric instead of Goalbook's criteria.",
    // Goal pages run it here; present levels and impact of disability run it in toolkit-vue3-spa,
    // whose modal reads the same stored selection. The links send a presenter to those two pages,
    // where the rubric differences show most; a goal page is already where this picker is live.
    demoLinks: [
      { label: 'Open the Present Levels Wizard to run this demo', url: toolkitUrls.presentlevels.v2 },
      { label: 'Compose an Impact of Disability statement to run this demo', url: toolkitUrls.impactOfDisability(true) }
    ],
    pagePatterns: GOAL_PAGES,
    categories: [
      { label: 'Full IEP checklist', profileId: 'georgia_dekalb' },
      { label: 'PLAAFP quality rubric', profileId: 'california-san_bernardino-draft' },
      { label: 'Regulation-cited rubric', profileId: 'maryland-stmarys' },
      { label: 'Impact and goals rubric', profileId: 'missouri_ssd' }
    ]
  }
]

export const isCustomizationOnPage = (customization, path) =>
  customization.pagePatterns.some((pattern) => pattern.test(path))
